import React from 'react';
import { Card, Typography, Badge, Tooltip, Tag, Grid, Button, Divider } from 'antd';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import {
    WhatsAppOutlined,
    FacebookOutlined,
    MessageOutlined,
    EditOutlined,
    DeleteOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined,
    CommentOutlined,
    ApiOutlined,
    BookOutlined,
    TeamOutlined,
    CopyOutlined,
} from '@ant-design/icons';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import AgentTestModal from '../AgentTestModal/AgentTestModal';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const getPlatformConfig = (channelType, t) => {
    switch (channelType?.toLowerCase()) {
        case 'whatsapp':
            return {
                icon: WhatsAppOutlined,
                color: '#25D366',
                name: t('whatsapp')
            };
        case 'messenger':
            return {
                icon: MessageOutlined,
                color: '#1877F2',
                name: t('messenger')
            };
        case 'facebook':
            return {
                icon: FacebookOutlined,
                color: '#1877F2',
                name: t('facebook')
            };
        default:
            return {
                icon: ApiOutlined,
                color: '#722ed1',
                name: channelType || t('channel')
            };
    }
};

const AgentCard = ({ agent, onStatusChange, onSettings, onDelete, onDuplicate, className }) => {
    const { t } = useTranslation();
    const screens = useBreakpoint() || {};
    const { isDarkMode } = useTheme();
    const isMobile = !!screens?.xs;
    const [loading, setLoading] = React.useState(false);
    const [testModalVisible, setTestModalVisible] = React.useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
    const [deactivateModalVisible, setDeactivateModalVisible] = React.useState(false);
    const isRunning = agent.is_started;

    const themeColors = {
        background: isDarkMode ? '#141414' : '#fff',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        secondaryText: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
        cardHover: isDarkMode ? '#1f1f1f' : '#fafafa',
        divider: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.06)',
        sectionBg: isDarkMode ? '#1d1d1d' : '#fafafa',
        tagBg: isDarkMode ? '#262626' : '#f0f0f0',
        tagBorder: isDarkMode ? '#434343' : '#d9d9d9',
    };

    const handleDelete = () => {
        setDeleteModalVisible(true);
    };

    const confirmDelete = () => {
        onDelete(agent.id);
        setDeleteModalVisible(false);
    };

    const localLoading = (state) => {
        setLoading(state);
    };

    const updateAgentStatus = (is_started) => {
        const updatedAgent = {
            is_started,
        };
        onStatusChange(updatedAgent, agent.id, localLoading);
    };

    const handleStatusToggle = () => {
        if (isRunning) {
            setDeactivateModalVisible(true);
            return;
        }

        updateAgentStatus(true);
    };

    const confirmDeactivate = () => {
        updateAgentStatus(false);
        setDeactivateModalVisible(false);
    };

    const handleTestAgent = () => {
        setTestModalVisible(true);
    };

    // Render channel tags with platform icons
    const renderChannelTags = () => {
        const channels = agent.channels || [];
        const maxVisible = 3;
        const visibleChannels = channels.slice(0, maxVisible);
        const remaining = channels.length - maxVisible;

        if (channels.length === 0) {
            return <Text type="secondary" style={{ fontSize: '12px' }}>{t('no_channels')}</Text>;
        }

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {visibleChannels.map((channel) => {
                    const platform = getPlatformConfig(channel.channel_type, t);
                    const PlatformIcon = platform.icon;
                    return (
                        <Tag
                            key={channel.id}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                margin: 0,
                                background: themeColors.tagBg,
                                border: `1px solid ${themeColors.tagBorder}`,
                                color: themeColors.text,
                            }}
                        >
                            <PlatformIcon style={{ color: platform.color, fontSize: '12px' }} />
                            <span style={{
                                maxWidth: '60px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>
                                {channel.name || platform.name}
                            </span>
                        </Tag>
                    );
                })}
                {remaining > 0 && (
                    <Tag style={{
                        margin: 0,
                        background: themeColors.tagBg,
                        border: `1px solid ${themeColors.tagBorder}`,
                        color: themeColors.secondaryText,
                    }}>
                        +{remaining}
                    </Tag>
                )}
            </div>
        );
    };

    // Render knowledge base tags
    const renderKnowledgeBaseTags = () => {
        const knowledgeBases = agent.knowledge_bases || [];
        const maxVisible = 3;
        const visibleKBs = knowledgeBases.slice(0, maxVisible);
        const remaining = knowledgeBases.length - maxVisible;

        if (knowledgeBases.length === 0) {
            return <Text type="secondary" style={{ fontSize: '12px' }}>{t('no_knowledge_bases')}</Text>;
        }

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {visibleKBs.map((kb) => (
                    <Tag
                        key={kb.id}
                        style={{
                            margin: 0,
                            maxWidth: '80px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            background: themeColors.tagBg,
                            border: `1px solid ${themeColors.tagBorder}`,
                            color: themeColors.text,
                        }}
                        title={kb.name}
                    >
                        {kb.name}
                    </Tag>
                ))}
                {remaining > 0 && (
                    <Tag style={{
                        margin: 0,
                        background: themeColors.tagBg,
                        border: `1px solid ${themeColors.tagBorder}`,
                        color: themeColors.secondaryText,
                    }}>
                        +{remaining}
                    </Tag>
                )}
            </div>
        );
    };

    // Render admin contact tags
    const renderAdminTags = () => {
        const admins = agent.admin_contacts || [];
        const maxVisible = 2;
        const visibleAdmins = admins.slice(0, maxVisible);
        const remaining = admins.length - maxVisible;

        if (admins.length === 0) {
            return <Text type="secondary" style={{ fontSize: '12px' }}>{t('no_admins')}</Text>;
        }

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {visibleAdmins.map((admin) => {
                    const name = admin.full_name || admin.email || t('unknown');
                    return (
                        <Tag
                            key={admin.id}
                            style={{
                                margin: 0,
                                maxWidth: '90px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                background: themeColors.tagBg,
                                border: `1px solid ${themeColors.tagBorder}`,
                                color: themeColors.text,
                            }}
                            title={name}
                        >
                            {name}
                        </Tag>
                    );
                })}
                {remaining > 0 && (
                    <Tag style={{
                        margin: 0,
                        background: themeColors.tagBg,
                        border: `1px solid ${themeColors.tagBorder}`,
                        color: themeColors.secondaryText,
                    }}>
                        +{remaining}
                    </Tag>
                )}
            </div>
        );
    };

    const cardActions = [
        isRunning ? (
            isMobile ? (
                <Button
                    type="text"
                    icon={loading ? <LoadingOutlined spin style={{ fontSize: '18px', color: '#1890ff' }} /> : <PauseCircleOutlined style={{ color: isDarkMode ? '#d8b732' : '#c7c709' }} />}
                    onClick={handleStatusToggle}
                    disabled={loading}
                    key="pause"
                />
            ) : (
                <Tooltip title={loading ? t('loading') : t('pause')} key="pause">
                    <Button
                        type="text"
                        icon={loading ? <LoadingOutlined spin style={{ fontSize: '18px', color: '#1890ff' }} /> : <PauseCircleOutlined style={{ color: isDarkMode ? '#d8b732' : '#c7c709' }} />}
                        onClick={handleStatusToggle}
                        disabled={loading}
                    />
                </Tooltip>
            )
        ) : (
            isMobile ? (
                <Button
                    type="text"
                    icon={loading ? <LoadingOutlined spin style={{ fontSize: '18px', color: '#1890ff' }} /> : <PlayCircleOutlined style={{ color: isDarkMode ? '#52c41a' : '#00C853' }} />}
                    onClick={handleStatusToggle}
                    disabled={loading}
                    key="play"
                />
            ) : (
                <Tooltip title={loading ? t('loading') : t('play')} key="play">
                    <Button
                        type="text"
                        icon={loading ? <LoadingOutlined spin style={{ fontSize: '18px', color: '#1890ff' }} /> : <PlayCircleOutlined style={{ color: isDarkMode ? '#52c41a' : '#00C853' }} />}
                        onClick={handleStatusToggle}
                        disabled={loading}
                    />
                </Tooltip>
            )
        ),
        isMobile ? (
            <Button
                type="text"
                icon={<CommentOutlined style={{ color: isDarkMode ? '#722ed1' : '#531dab' }} />}
                onClick={handleTestAgent}
                key="test"
            />
        ) : (
            <Tooltip title={t('test_agent')} key="test">
                <Button
                    type="text"
                    icon={<CommentOutlined style={{ color: isDarkMode ? '#722ed1' : '#531dab' }} />}
                    onClick={handleTestAgent}
                />
            </Tooltip>
        ),
        isMobile ? (
            <Button
                type="text"
                icon={<EditOutlined style={{ color: isDarkMode ? '#177ddc' : '#1890ff' }} />}
                onClick={() => onSettings(agent)}
                key="edit"
            />
        ) : (
            <Tooltip title={t('edit')} key="edit">
                <Button
                    type="text"
                    icon={<EditOutlined style={{ color: isDarkMode ? '#177ddc' : '#1890ff' }} />}
                    onClick={() => onSettings(agent)}
                />
            </Tooltip>
        ),
        isMobile ? (
            <Button
                type="text"
                icon={<CopyOutlined style={{ color: isDarkMode ? '#13c2c2' : '#08979c' }} />}
                onClick={() => onDuplicate(agent)}
                key="duplicate"
            />
        ) : (
            <Tooltip title={t('duplicate')} key="duplicate">
                <Button
                    type="text"
                    icon={<CopyOutlined style={{ color: isDarkMode ? '#13c2c2' : '#08979c' }} />}
                    onClick={() => onDuplicate(agent)}
                />
            </Tooltip>
        ),
        isMobile ? (
            <Button
                type="text"
                icon={<DeleteOutlined style={{ color: isDarkMode ? '#d32029' : '#ff4d4f' }} />}
                onClick={handleDelete}
                key="delete"
            />
        ) : (
            <Tooltip title={t('delete')} key="delete">
                <Button
                    type="text"
                    icon={<DeleteOutlined style={{ color: isDarkMode ? '#d32029' : '#ff4d4f' }} />}
                    onClick={handleDelete}
                />
            </Tooltip>
        ),
    ];

    const sectionStyle = {
        marginBottom: '12px',
    };

    const sectionHeaderStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '6px',
        color: themeColors.secondaryText,
        fontSize: '12px',
    };

    return (
        <Card
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: isMobile ? '100%' : '280px',
                background: themeColors.background,
                border: `1px solid ${themeColors.divider}`,
            }}
            className={className}
            bordered={false}
            styles={{
                body: {
                    padding: '16px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
            actions={cardActions}
        >
            {/* Header: Agent Name + Status */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
            }}>
                <Text
                    strong
                    style={{
                        fontSize: '16px',
                        flex: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: themeColors.text,
                        marginInlineEnd: '8px',
                    }}
                    title={agent.name}
                >
                    {agent.name}
                </Text>
                <Badge
                    status={isRunning ? "success" : "error"}
                    text={
                        <Text style={{
                            fontSize: '12px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            background: isRunning ?
                                (isDarkMode ? '#162312' : '#f6ffed') :
                                (isDarkMode ? '#2a1215' : '#fff2f0'),
                            color: isRunning ?
                                (isDarkMode ? '#49aa19' : '#52c41a') :
                                (isDarkMode ? '#d32029' : '#ff4d4f')
                        }}>
                            {isRunning ? t('started') : t('stopped')}
                        </Text>
                    }
                />
            </div>

            <Divider style={{ margin: '0 0 12px 0', borderColor: themeColors.divider }} />

            {/* Channels Section */}
            <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                    <ApiOutlined style={{ fontSize: '14px' }} />
                    <span>{t('channels')}</span>
                </div>
                {renderChannelTags()}
            </div>

            {/* Knowledge Bases Section */}
            <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>
                    <BookOutlined style={{ fontSize: '14px' }} />
                    <span>{t('knowledge_bases')}</span>
                </div>
                {renderKnowledgeBaseTags()}
            </div>

            {/* Admins Section */}
            <div style={{ ...sectionStyle, marginBottom: 0, flex: 1 }}>
                <div style={sectionHeaderStyle}>
                    <TeamOutlined style={{ fontSize: '14px' }} />
                    <span>{t('admins')}</span>
                </div>
                {renderAdminTags()}
            </div>

            {testModalVisible && (
                <AgentTestModal
                    visible={testModalVisible}
                    onClose={() => setTestModalVisible(false)}
                    agent={agent}
                />
            )}

            {/* Delete Agent Modal */}
            <ResponsiveModal
                title={t('delete_agent')}
                open={deleteModalVisible}
                onOk={confirmDelete}
                onCancel={() => setDeleteModalVisible(false)}
                okText={t('delete')}
                cancelText={t('cancel')}
                okButtonProps={{ danger: true }}
                desktopWidth={420}
            >
                <div>{t('are_you_sure_you_want_to_delete_agent', { name: agent.name })}</div>
            </ResponsiveModal>

            <ResponsiveModal
                title={t('deactivate_agent')}
                open={deactivateModalVisible}
                onOk={confirmDeactivate}
                onCancel={() => setDeactivateModalVisible(false)}
                okText={t('deactivate')}
                cancelText={t('cancel')}
                okButtonProps={{ danger: true }}
                desktopWidth={420}
            >
                <div>{t('are_you_sure_you_want_to_deactivate_agent', { name: agent.name })}</div>
            </ResponsiveModal>
        </Card>
    );
};

export default AgentCard;
